import React, { useContext, useEffect } from "react";
import { useStripeCheckout } from "../hooks/useStripeCheckout";
import photoSmallMe from "../static/me-small.png";
import { overview } from "../content/classes";
import Layout from "../components/layout";
import "../tailwind.css";
import "../index.css";
import { API } from "aws-amplify";
import { Helmet } from "react-helmet";
import { Section } from "../components/Layout/Section";
import { Link as AnchorLink } from "react-scroll";
import { PageTitle } from "../components/Common/PageTitle";
import ReactMarkdown from "react-markdown";
import { LanguageToggle } from "../components/Common/LanguageToggle";
import { AppContext } from "../context";

const t = {
  nl: {
    buyNow: "Koop Nu",
    buyClass: "Koop Klas",
    soundGood: "Klinkt goed? Wacht dan niet en",
    buyTheClass: "Klinkt goed? Wacht dan niet en koop de klas!",
    youHave2Options: "Je hebt 2 opties:",
    waxOnly: "Alleen was.",
    waxOnlyDescription:
      "Het hele process van hoe je de onderdelen in was snijdt.",
    waxAndCasting: "Was en afwerking gieten.",
    waxAndCastingDescription:
      "Het hele process van hoe je de onderdelen in was snijdt. PLUS 3 extra video's die laten zien hoe je de gietingen schoonmaakt EN hoe je ze polijst en afwerkt.",
    boughtWaxOnly:
      "De alleen-was klas gekocht en wil je de extra afwerking video's? Je kunt het verschil betalen om er toegang toe te krijgen. De optie om te upgraden zit in de klas.",
    tutor: "Docent",
    alwaysKnow: "Altijd weten waar ik mee bezig ben!",
    getWeeklyEmails: "Krijg wekelijkse emails!",
    morning: "ochtend",
    afternoon: "middag",
    selectWorkshop: "Selecteer jouw workshop...",
    amount: "Aantal",
    between: "Tussen 1 en 6",
    yourChoice: "Jouw keuze:",
    date: "Datum",
    items: "Aantal",
    total: "Totaal",
    processing: "Bezig...",
    selectWorkshopToContinue: "Selecteer workshop om verder te gaan",
    soldOut: "Uitverkocht",
    left: "over",
    contact: "Neem Contact Op",
  },
  en: {
    buyNow: "Buy Now",
    buyClass: "Buy Class",
    soundGood: "Sound good? Then don't wait and",
    buyTheClass: "Sound good? Then don't wait and buy the class!",
    youHave2Options: "You have 2 options:",
    waxOnly: "Wax only.",
    waxOnlyDescription: "The entire process of how to carve the pieces in wax.",
    waxAndCasting: "Wax and casting clean up.",
    waxAndCastingDescription:
      "The entire process of how to carve the pieces in wax. PLUS 3 additional videos showing how to clean up the castings AND how to polish and finish them.",
    boughtWaxOnly:
      "Bought the wax only class and want the additional casting clean up videos? You can pay the difference to get access to them. The option to upgrade is inside the class.",
    tutor: "Tutor",
    alwaysKnow: "Always know what I'm up to!",
    getWeeklyEmails: "Get weekly emails!",
    morning: "morning",
    afternoon: "afternoon",
    selectWorkshop: "Select your workshop...",
    amount: "Quantity",
    between: "Between 1 and 6",
    yourChoice: "Your selection:",
    date: "Date",
    items: "Quantity",
    total: "Total",
    processing: "Processing...",
    selectWorkshopToContinue: "Select a workshop to continue",
    soldOut: "Sold Out",
    left: "left",
    contact: "Contact Me",
  },
};

const importAll = (r, cache) =>
  r.keys().forEach((key) => (cache[key] = r(key)));
const imageFiles = {};
importAll(
  require.context(`../content/salesPages/in-person/`, true, /\.(png|gif|jpg)$/),
  imageFiles,
);
const transformImageUriBuilder = (slug) => (uri) => {
  if (uri) {
    const imgPath = `./${slug}/${uri.substring(2)}`;
    const importedImg = imageFiles[imgPath];
    return importedImg ? importedImg.default : "";
  }
};

const Divider = ({ text, margins = {} }) => (
  <div
    className={`relative ${margins.b ? "mb-5" : ""} ${
      margins.t ? "mt-10" : ""
    }`}
  >
    <div className="absolute inset-0 flex items-center" aria-hidden="true">
      <div className="w-full border-t border-gray-300"></div>
    </div>
    <div className="relative flex justify-center">
      <span className="px-3 bg-white text-lg font-medium text-gray-900">
        {text}
      </span>
    </div>
  </div>
);

const Spinny = ({
  text = "Creating Order",
  nomt = false,
  extraClasses = "",
}) => (
  <span
    className={`${
      nomt ? " " : "mt-8"
    } ${extraClasses} w-full inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-blue-700 bg-white hover:bg-indigo-50 sm:w-auto`}
  >
    <svg
      className="animate-spin w-6 h-6"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
    >
      <path
        fillRule="evenodd"
        d="M9.504 1.132a1 1 0 01.992 0l1.75 1a1 1 0 11-.992 1.736L10 3.152l-1.254.716a1 1 0 11-.992-1.736l1.75-1zM5.618 4.504a1 1 0 01-.372 1.364L5.016 6l.23.132a1 1 0 11-.992 1.736L4 7.723V8a1 1 0 01-2 0V6a.996.996 0 01.52-.878l1.734-.99a1 1 0 011.364.372zm8.764 0a1 1 0 011.364-.372l1.733.99A1.002 1.002 0 0118 6v2a1 1 0 11-2 0v-.277l-.254.145a1 1 0 11-.992-1.736l.23-.132-.23-.132a1 1 0 01-.372-1.364zm-7 4a1 1 0 011.364-.372L10 8.848l1.254-.716a1 1 0 11.992 1.736L11 10.58V12a1 1 0 11-2 0v-1.42l-1.246-.712a1 1 0 01-.372-1.364zM3 11a1 1 0 011 1v1.42l1.246.712a1 1 0 11-.992 1.736l-1.75-1A1 1 0 012 14v-2a1 1 0 011-1zm14 0a1 1 0 011 1v2a1 1 0 01-.504.868l-1.75 1a1 1 0 11-.992-1.736L16 13.42V12a1 1 0 011-1zm-9.618 5.504a1 1 0 011.364-.372l.254.145V16a1 1 0 112 0v.277l.254-.145a1 1 0 11.992 1.736l-1.735.992a.995.995 0 01-1.022 0l-1.735-.992a1 1 0 01-.372-1.364z"
        clipRule="evenodd"
      />
    </svg>
    &nbsp;
    {text}
  </span>
);

const ContactButton = ({ lang }) => (
  <>
    <div className="mt-10 pb-8 rounded" id="purchase">
      <div className="w-full text-center py-3 px-4">
        <a
          href="mailto:info@waxcarvers.com"
          className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-700 hover:bg-blue-900 transition-colors duration-200"
        >
          {t[lang].contact}
        </a>
      </div>
    </div>
  </>
);

const SelectionPriceCard = ({
  productId,
  price,
  lang = "en",
  slots,
  slotIdentifier,
}) => {
  const [timeSlot, setTimeSlot] = React.useState(slotIdentifier ?? "");
  const [itemCount, setItemCount] = React.useState(1);
  const [limitedItemsData, setLimitedItemsData] = React.useState([]);

  const hasSlots =
    slots &&
    slots.dates &&
    Array.isArray(slots.dates) &&
    slots.dates.length > 0;

  if (!hasSlots && !productId) {
    return null;
  }

  const { state: contextState } = useContext(AppContext);
  const products = contextState ? contextState.products : [];
  const product = products.filter((it) => {
    return it.id === productId;
  });

  const prices = product[0]?.prices ?? [];
  const configuredUnitPrice = Number.isFinite(price) ? price : undefined;
  const lowestPrice = React.useMemo(() => {
    if (prices.length === 0) return undefined;
    return prices.reduce(
      (lowest, current) => (current.amount < lowest.amount ? current : lowest),
      prices[0],
    );
  }, [prices]);
  const configuredPriceObject = React.useMemo(() => {
    if (!configuredUnitPrice || prices.length === 0) return undefined;
    return prices.find(
      (it) => Math.round((it.amount ?? 0) / 100) === configuredUnitPrice,
    );
  }, [configuredUnitPrice, prices]);
  const baseUnitPrice =
    configuredUnitPrice ??
    (lowestPrice?.amount ? Math.round(lowestPrice.amount / 100) : 25);
  const totalPrice = baseUnitPrice * itemCount;

  const priceId = React.useMemo(() => {
    if (configuredPriceObject?.id) return configuredPriceObject.id;
    if (prices.length === 0) return undefined;
    return lowestPrice.id;
  }, [configuredPriceObject, prices, lowestPrice]);

  const { loading, redirectToCheckout } = useStripeCheckout(
    {
      id: productId,
    },
    hasSlots
      ? {
          price_id: priceId,
          quantity: itemCount,
          withShipping: false,
          custom_text: {
            submit: {
              message: timeSlot
                ? `Time Slot: ${timeSlot} | Items: ${itemCount}`
                : `Items: ${itemCount}`,
            },
          },
        }
      : {},
    hasSlots || slotIdentifier
      ? {
          timeSlot,
          itemCount,
        }
      : {},
  );

  const timeSlotsLocal = React.useMemo(() => {
    if (!hasSlots) return [];

    const fmt = (v) => {
      const parsed = new Date(v);
      if (Number.isNaN(parsed.getTime())) return v;
      return parsed.toLocaleDateString(lang === "nl" ? "nl-NL" : "en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    };

    const fmtSlotDate = (date) => {
      const start = date.startDate ?? date.value;
      const end = date.endDate ?? start;
      if (!start) return "";
      if (start === end) return fmt(start);
      const separator = lang === "nl" ? " t/m " : " to ";
      return `${fmt(start)}${separator}${fmt(end)}`;
    };

    return slots.dates.flatMap((date) => {
      if (Array.isArray(date.options) && date.options.length > 0) {
        return date.options.map((option, optionIndex) => ({
          id:
            option.id ??
            `${date.value}_${option.key ?? option.value ?? optionIndex}`,
          value:
            option.value ??
            `${date.value}-${option.key ?? option.label ?? optionIndex}`,
          label: option.fullLabel ?? `${fmtSlotDate(date)} (${option.label})`,
        }));
      }

      const slotTimeLabel = date.timeLabel ?? "14:00 - 17:30";
      const slotValue = date.slotValue ?? `${date.value}-afternoon`;

      return [
        {
          id: date.id ?? `${date.value}_${slotTimeLabel}`,
          value: slotValue,
          label: date.fullLabel ?? `${fmtSlotDate(date)} (${slotTimeLabel})`,
        },
      ];
    });
  }, [lang, hasSlots, slots]);

  const selectedTimeSlot = timeSlotsLocal.find(
    (slot) => slot.value === timeSlot,
  );

  React.useEffect(() => {
    const commsApi = "limiteditems";
    const getItemLimits = async () => {
      try {
        const response = await API.get(commsApi, `/limited-items`);
        if (response && Array.isArray(response)) {
          setLimitedItemsData(response);
        }
      } catch (error) {
        console.log("Limited items API not available");
      }
    };
    getItemLimits();
  }, []);

  const getRemainingItems = (timeSlotValue) => {
    const itemData = limitedItemsData.find(
      (item) => item.item === timeSlotValue,
    );
    if (!itemData) return 6;
    const purchasedCount = parseInt(itemData.count) || 0;
    return Math.max(0, 6 - purchasedCount);
  };

  const handleCheckout = () => {
    redirectToCheckout();
  };

  const isCheckoutDisabled = hasSlots && !timeSlot;

  // If no slots data, render simple single purchase
  if (!hasSlots) {
    const remainingItems = getRemainingItems(slotIdentifier);

    return (
      <div className="mt-10 rounded" id="purchase">
        <div className="w-full text-center py-3 px-4">
          {loading ? (
            <Spinny />
          ) : (
            <>
              {slotIdentifier && (
                <p className="mb-4 text-gray-600">
                  {remainingItems} {t[lang].left}
                </p>
              )}
              <button
                onClick={redirectToCheckout}
                disabled={remainingItems === 0}
                className={`inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white ${
                  remainingItems === 0
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-700 hover:bg-blue-900"
                } transition-colors duration-200`}
              >
                {remainingItems === 0
                  ? t[lang].soldOut
                  : `${t[lang].buyClass} €${price ?? 360}`}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="my-10 rounded">
      <div
        id="purchase"
        className="bg-blue-100 mt-2 relative max-w-screen-lg mx-auto py-8 px-4 sm:px-6 lg:px-8 rounded-md"
      >
        <div className="max-w-md mx-auto lg:max-w-3xl">
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {t[lang].selectWorkshop}
            </h3>

            {/* Time slot dropdown */}
            <div className="mb-4">
              <select
                id="timeSlot"
                value={timeSlot}
                onChange={(e) => setTimeSlot(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                required
              >
                <option value="">{t[lang].selectWorkshop}</option>
                {timeSlotsLocal.map((slot) => {
                  const remainingItems = getRemainingItems(slot.value);
                  const isUnavailable = remainingItems === 0;

                  return (
                    <option
                      key={slot.value}
                      value={slot.value}
                      disabled={isUnavailable}
                    >
                      {slot.label}{" "}
                      {isUnavailable
                        ? `(${t[lang].soldOut})`
                        : `(${remainingItems} ${t[lang].left})`}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Item count */}
            <div className="mb-6">
              {/* Item count selection */}
              <div>
                <label
                  htmlFor="itemCount"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t[lang].amount}
                </label>
                <div className="relative flex items-center max-w-xs">
                  <button
                    type="button"
                    onClick={() => setItemCount(Math.max(1, itemCount - 1))}
                    className="absolute left-0 top-0 bottom-0 px-3 flex items-center justify-center bg-gray-50 hover:bg-gray-100 border border-r-0 border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  >
                    <svg
                      className="w-4 h-4 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M20 12H4"
                      />
                    </svg>
                  </button>
                  <input
                    id="itemCount"
                    type="number"
                    min="1"
                    max="6"
                    value={itemCount}
                    onChange={(e) =>
                      setItemCount(
                        Math.max(1, Math.min(6, parseInt(e.target.value) || 1)),
                      )
                    }
                    className="block w-full px-10 py-2 border border-gray-300 text-center focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setItemCount(Math.min(6, itemCount + 1))}
                    className="absolute right-0 top-0 bottom-0 px-3 flex items-center justify-center bg-gray-50 hover:bg-gray-100 border border-l-0 border-gray-300 rounded-r-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  >
                    <svg
                      className="w-4 h-4 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500">{t[lang].between}</p>
              </div>
            </div>

            {/* Selection summary */}
            {(timeSlot || itemCount > 1) && (
              <div className="mt-4 p-3 bg-gray-50 rounded-md mb-6">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  {t[lang].yourChoice}
                </h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  {timeSlot && selectedTimeSlot && (
                    <li>
                      • {t[lang].date}: {selectedTimeSlot.label}
                    </li>
                  )}
                  <li>
                    • {t[lang].items}: {itemCount}
                  </li>
                </ul>
                <div className="mt-2 pt-2 border-gray-300">
                  <p className="text-sm font-medium text-gray-900">
                    {t[lang].total}: €{totalPrice}
                  </p>
                </div>
              </div>
            )}

            {/* Checkout Button */}
            <div className="flex justify-center">
              {loading ? (
                <Spinny text={t[lang].processing} />
              ) : (
                <button
                  onClick={handleCheckout}
                  disabled={isCheckoutDisabled}
                  className={`inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white ${
                    isCheckoutDisabled
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-blue-700 hover:bg-blue-900"
                  } transition-colors duration-200`}
                >
                  {isCheckoutDisabled
                    ? t[lang].selectWorkshopToContinue
                    : `${t[lang].buyClass} - €${totalPrice}`}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ClassSalesPage = ({ pageContext: { page, slug, config, slots } }) => {
  const [lang, setLang] = React.useState("nl");
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const showContactButton = config.showContactButton !== false;
  const showTopButton = config.showTopButton !== false;

  // Helper function to get language-specific content
  const getLocalizedContent = React.useCallback(
    (contentKey) => {
      if (!page) {
        console.warn("Page data is not available");
        return { attributes: { title: "", subtitle: "" }, body: "" };
      }

      let content;
      if (lang === "en") {
        const englishKey = `${contentKey}_en`;
        content = page[englishKey] || page[contentKey];
      } else {
        content = page[contentKey];
      }

      if (!content) {
        console.warn(
          `Content not found for key: ${contentKey}, language: ${lang}`,
        );
        return {
          attributes: {
            title: `[Missing: ${contentKey}]`,
            subtitle: "",
            image: "",
            imageAlt: "",
          },
          body: `Content not found for ${contentKey}`,
        };
      }

      console.log(`Successfully loaded content for: ${contentKey}`);
      return content;
    },
    [lang, page],
  );

  const bannerImg =
    "http://images.waxcarvers.com.s3-website-eu-west-1.amazonaws.com/waxcarvers.png";
  const imageFile = imageFiles[`./${slug}/${config.image}`]
    ? imageFiles[`./${slug}/${config.image}`].default
    : bannerImg;

  const { productId, price, slotIdentifier } = config;
  const pricingNote =
    lang === "en"
      ? config.pricingNote_en ?? config.pricingNote
      : config.pricingNote_nl ?? config.pricingNote;

  const transformImageUri = transformImageUriBuilder(slug);
  const richContent = {
    "@context": "https://schema.org/",
    "@type": "Course",
    name: config.title,
    description: config.description,
    thumbnailUrl: `https://www.waxcarvers.com/${imageFile}`,

    hasCourseInstance: {
      "@type": "CourseInstance",
      courseMode: ["onsite"],
    },
    provider: {
      "@type": "Organization",
      name: "Wax Carvers",
      logo: {
        "@type": "ImageObject",
        url: bannerImg,
      },
    },
    author: {
      "@type": "Person",
      name: "Sandy Pfaff",
    },
  };

  return (
    <Layout full>
      <Helmet>
        <title>{config.title}</title>
        <meta name="description" content={config.description} />
        <meta property="og:title" content={`${config.title} | Wax Carvers`} />
        <meta property="og:description" content={config.description} />
        <meta
          property="og:image"
          content={`https://www.waxcarvers.com/${imageFile}`}
        />
        <meta
          property="og:url"
          content={`https://www.waxcarvers.com/class/${slug}`}
        />

        <meta itemProp="name" content={`${config.title} | Wax Carvers`} />
        <meta itemProp="description" content={config.description} />

        <meta
          property="twitter:title"
          content={`${config.title} | Wax Carvers`}
        />
        <meta property="twitter:description" content={config.description} />
        <meta
          property="twitter:card"
          content={`https://www.waxcarvers.com/${imageFile}`}
        />
        <meta
          property="twitter:image:src"
          content={`https://www.waxcarvers.com/${imageFile}`}
        />

        <script type="application/ld+json">
          {JSON.stringify(richContent)}
        </script>
      </Helmet>
      <div className="bg-white">
        <div className="mt-12">
          <LanguageToggle lang={lang} setLang={setLang} />
          <PageTitle
            title={getLocalizedContent("overview").attributes.title}
            subtitle={getLocalizedContent("overview").attributes.subtitle}
          />
        </div>
        <main>
          <div className="hidden sm:max-w-3xl sm:mx-auto sm:p-6 sm:block lg:p-8">
            <div className="relative shadow-xl sm:overflow-hidden">
              <div className="absolute inset-0">
                <img
                  src={transformImageUri(page.overview.attributes.image)}
                  alt="Class overview image"
                  className="h-full w-full object-cover"
                />

                <div className="absolute inset-0 mix-blend-multiply"></div>
              </div>
              <div className="relative px-4 py-64 sm:px-6 sm:p-24 lg:p-32 lg:px-8">
                <div className="py-16">
                  <div className="mt-10 lg:flex-shrink-0 mx-12">
                    <div className=" text-center"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="sm:hidden mx-auto sm:p-6">
            <img
              src={transformImageUri(page.overview.attributes.image)}
              alt="Class overview image"
              className="h-full w-full object-cover"
            />
          </div>

          <div className="bg-blue-600 md:mt-4">
            <div className="max-w-4xl mx-auto py-6 px-4 sm:px-4 lg:px-4">
              {getLocalizedContent("overview").attributes.asMarkdown ? (
                <p className="text-xl text-white tracking-wide text-left whitespace-pre-wrap">
                  <ReactMarkdown
                    allowDangerousHtml
                    components={{
                      blockquote: ({ node, ...props }) => {
                        return (
                          <span style={{ fontSize: 30 }}>
                            <em {...props} />
                          </span>
                        );
                      },
                    }}
                  >
                    {getLocalizedContent("overview").body || ""}
                  </ReactMarkdown>
                </p>
              ) : (
                <p className="text-xl text-white tracking-wide text-left whitespace-pre-wrap">
                  {getLocalizedContent("overview").body || ""}
                </p>
              )}
            </div>
            <div className="lg:flex-shrink-0 mx-12">
              {showTopButton && (
                <div className="rounded-md text-center">
                  {productId && price ? (
                    <AnchorLink
                      to="purchase"
                      smooth={true}
                      offset={-100}
                      duration={500}
                    >
                      <div className="mt-16 mb-8 mx-auto text-lg sm:text-xl font-medium cursor-pointer inline-flex items-center justify-center py-4 px-8 border border-transparent rounded-md text-white bg-blue-700 hover:bg-blue-900 transition-colors duration-200">
                        {t[lang].buyNow}
                      </div>
                    </AnchorLink>
                  ) : (
                    showContactButton && <ContactButton lang={lang} />
                  )}
                </div>
              )}
            </div>
          </div>

          {getLocalizedContent("techniques").body && (
            <Section
              item={{
                title: getLocalizedContent("techniques").attributes.title,
                text: getLocalizedContent("techniques").body || "",
                image: transformImageUri(page.techniques.attributes.image),
                imageAlt:
                  getLocalizedContent("techniques").attributes.imageAlt ??
                  getLocalizedContent("techniques").attributes.title,
              }}
              transformImageUri={transformImageUri}
              asMarkdown={page.techniques.attributes.asMarkdown}
              leading={page.techniques.attributes.leading}
            />
          )}
          {getLocalizedContent("tools").body && (
            <>
              <Section
                item={{
                  orientation: "right",
                  title: getLocalizedContent("tools").attributes.title,
                  text: getLocalizedContent("tools").body || "",
                  image: transformImageUri(page.tools.attributes.image),
                  imageAlt:
                    getLocalizedContent("tools").attributes.imageAlt ??
                    getLocalizedContent("tools").attributes.title,
                }}
                transformImageUri={transformImageUri}
                asMarkdown={true}
              />

              <Divider margins={{ t: true, b: true }} />
            </>
          )}
          <div className="lg:flex-shrink-0 m-12">
            <div className="rounded-md text-center my-12">
              <SelectionPriceCard
                lang={lang}
                slots={slots}
                productId={productId}
                price={price}
                slotIdentifier={slotIdentifier}
              />
              {pricingNote && (
                <div className="mt-6 text-base text-gray-700">
                  <ReactMarkdown
                    components={{
                      p: ({ node, ...props }) => (
                        <p
                          className="whitespace-pre-line mb-4 last:mb-0"
                          {...props}
                        />
                      ),
                      a: ({ node, ...props }) => (
                        <a
                          className="text-blue-700 hover:text-blue-900 underline"
                          {...props}
                        />
                      ),
                    }}
                  >
                    {pricingNote}
                  </ReactMarkdown>
                </div>
              )}
            </div>
          </div>

          <section className="relative bg-white" aria-labelledby="join-heading">
            <div
              className="hidden absolute inset-x-0 h-1/2 bg-blue-gray-50 lg:block"
              aria-hidden="true"
            ></div>
            <div className="max-w-7xl mx-auto  md:bg-transparent lg:px-8">
              <div className="lg:grid lg:grid-cols-12">
                <div className="relative z-10 lg:col-start-1 lg:row-start-1 lg:col-span-4 lg:py-16 lg:bg-transparent">
                  <div
                    className="absolute inset-x-0 h-1/2 bg-blue-gray-50 lg:hidden"
                    aria-hidden="true"
                  ></div>
                  <div className="mx-auto sm:max-w-3xl sm:px-6 lg:max-w-none lg:p-0">
                    <div className="aspect-w-10 aspect-h-8 sm:aspect-w-2 md:aspect-h-2 lg:aspect-w-1 lg:aspect-h-1">
                      <img
                        className="object-cover object-center md:rounded-3xl shadow-2xl"
                        src={photoSmallMe}
                        alt="Photo of me, Sandy."
                      />
                    </div>
                  </div>
                </div>

                <div className="relative bg-blue-600 lg:col-start-3 lg:row-start-1 lg:col-span-10 lg:rounded-3xl lg:grid lg:grid-cols-10 lg:items-center">
                  <div className="relative max-w-md mx-auto py-12 px-4 space-y-6 sm:max-w-3xl sm:py-16 sm:px-6 lg:max-w-none lg:p-0 lg:col-start-4 lg:col-span-6">
                    <h2 className="text-3xl text-white" id="join-heading">
                      {t[lang].tutor}
                    </h2>
                    <p className="text-lg text-white whitespace-pre-wrap">
                      {page && page.tutor ? page.tutor.body : ""}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section>
            <div className="bg-blue-100 lg:py-5 rounded mt-12">
              <div className="max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8 lg:flex lg:items-center lg:justify-center">
                <h2 className="text-2xl text-bold tracking-tight text-gray-700 text-center">
                  <span className="block">{t[lang].alwaysKnow}</span>
                </h2>
                <div className="lg:mt-0 lg:flex-shrink-0 mx-12 mt-8">
                  <div className="rounded-md text-center">
                    <a
                      href="https://newsletter.waxcarvers.com"
                      aria-label="Join newsletter"
                      className="inline-flex items-center justify-center px-5 py-3 border border-transparent
                         text-base font-medium rounded-md text-white bg-blue-700 hover:bg-blue-900"
                    >
                      {t[lang].getWeeklyEmails}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>
    </Layout>
  );
};
export default ClassSalesPage;
